import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// Backend API URL - uses environment variable or defaults to localhost
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Sample trip cards for the right panel
const SAMPLE_TRIPS = [
  {
    flag: '🇫🇴', dest: 'Faroe Islands', days: 5, dayNum: 2,
    title: 'Driving the edge of the world',
    activities: [
      { time: '8:00 AM', name: 'Múlafossur Waterfall' },
      { time: '11:00 AM', name: 'Sørvágsvatn Lake' },
      { time: '2:00 PM', name: 'Lunch at Áarstova' },
      { time: '4:00 PM', name: 'Tindhólmur viewpoint' },
    ],
    color: '#1d8e85',
  },
];

// Topographic SVG data URI for background texture
const TOPO_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 900' preserveAspectRatio='none'%3E%3Cpath d='M0 120 Q300 60 600 140 T1200 100' fill='none' stroke='%230d9488' stroke-width='1.2'/%3E%3Cpath d='M0 200 Q250 160 550 220 T1200 180' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 300 Q400 250 700 320 T1200 280' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 400 Q350 370 650 420 T1200 390' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 520 Q300 480 600 540 T1200 510' fill='none' stroke='%23313143' stroke-width='1.2'/%3E%3Cpath d='M0 640 Q400 600 750 660 T1200 630' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 750 Q250 720 550 770 T1200 740' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 850 Q350 810 700 870 T1200 840' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3C/svg%3E";

const FullPageLogin = ({ referralCode: referralCodeProp }) => {
  const { signIn, error, loading } = useAuth();
  // Skip referral code step if user has already validated a code before
  const hasStoredReferral = (() => {
    try { return !!localStorage.getItem('arrivo_referred_by'); } catch { return false; }
  })();
  const [step, setStep] = useState(hasStoredReferral ? 'login' : 'code'); // 'code' | 'login'
  const [inviteCode, setInviteCode] = useState(referralCodeProp || '');
  const [codeError, setCodeError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsError, setTermsError] = useState('');
  const autoValidatedRef = React.useRef(false);

  // Auto-validate if referral code was passed from URL
  React.useEffect(() => {
    if (referralCodeProp && !autoValidatedRef.current && step === 'code') {
      autoValidatedRef.current = true;
      validateCode(referralCodeProp);
    }
  }, [referralCodeProp]);

  const validateCode = async (codeToValidate) => {
    const trimmed = (codeToValidate || inviteCode).trim();
    if (!trimmed) return;

    setIsValidating(true);
    setCodeError('');

    try {
      const response = await fetch(`${API_URL}/api/validate-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed }),
      });

      const data = await response.json();

      if (data.valid) {
        // Store referral code for post-signup profile update
        if (data.referral_code) {
          try { localStorage.setItem('arrivo_referred_by', data.referral_code); } catch {}
        }
        // Clean up pending referral
        try { sessionStorage.removeItem('arrivo_pending_referral'); } catch {}
        setStep('login');
      } else {
        setCodeError('Invalid code. Reach out to hello@arrivotravel.ai or @arrivotravel.ai on Instagram with your favorite 3 travel destinations for a referral code.');
      }
    } catch (err) {
      console.error('Error validating code:', err);
      setCodeError('Unable to validate code. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    validateCode();
  };

  const handleGoogleSignIn = async () => {
    if (!acceptedTerms) {
      setTermsError('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    if (!ageConfirmed) {
      setTermsError('Please confirm you are 16 years of age or older');
      return;
    }

    setIsSigningIn(true);
    setTermsError('');
    try {
      await signIn();
    } catch (err) {
      console.error('Sign in error:', err);
    } finally {
      setIsSigningIn(false);
    }
  };


  return (
    <div className="min-h-screen flex relative overflow-hidden" style={{ background: '#F5F5F0' }}>
      {/* Topographic wave accent — top */}
      <svg className="absolute top-0 left-0 w-full pointer-events-none" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{height: '220px', zIndex: 0}}>
        <path fill="#0d9488" fillOpacity="0.06" d="M0,160L48,170.7C96,181,192,203,288,192C384,181,480,139,576,128C672,117,768,139,864,170.7C960,203,1056,245,1152,240C1248,235,1344,181,1392,154.7L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"/>
        <path fill="#14b8a6" fillOpacity="0.04" d="M0,96L48,112C96,128,192,160,288,165.3C384,171,480,149,576,149.3C672,149,768,171,864,176C960,181,1056,171,1152,144C1248,117,1344,75,1392,53.3L1440,32L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"/>
        <path fill="#0d9488" fillOpacity="0.03" d="M0,64L48,80C96,96,192,128,288,138.7C384,149,480,139,576,117.3C672,96,768,64,864,69.3C960,75,1056,117,1152,133.3C1248,149,1344,139,1392,133.3L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"/>
      </svg>
      {/* Topographic wave accent — bottom */}
      <svg className="absolute bottom-0 left-0 w-full pointer-events-none" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{height: '180px', zIndex: 0}}>
        <path fill="#0d9488" fillOpacity="0.05" d="M0,224L48,213.3C96,203,192,181,288,186.7C384,192,480,224,576,218.7C672,213,768,171,864,165.3C960,160,1056,192,1152,197.3C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"/>
        <path fill="#14b8a6" fillOpacity="0.03" d="M0,256L48,250.7C96,245,192,235,288,218.7C384,203,480,181,576,192C672,203,768,245,864,250.7C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"/>
      </svg>
      {/* Left side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 sm:px-12 lg:px-16 xl:px-20 relative z-10" style={{ background: '#F5F5F0' }}>
        {/* Subtle topo bg */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `url("${TOPO_SVG}")`,
          backgroundSize: 'cover',
          opacity: 0.03,
        }} />
        <div className="max-w-md w-full relative z-10 rounded-[28px] p-10" style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', boxShadow: '0 22px 60px rgba(28,41,53,0.07)' }}>
          {/* Logo */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-2">
              <img src="/arrivo-icon.png" alt="Arrivo" className="h-12 w-auto object-contain" />
              <h1 className="text-3xl font-extrabold" style={{ fontFamily: "'Gulfs Display', 'Batangas', sans-serif", color: '#313143', letterSpacing: '0.05em' }}>
                arrivo
              </h1>
            </div>
            <p className="text-sm font-medium" style={{ color: '#64748b', letterSpacing: '-0.01em' }}>
              plan less. explore more.
            </p>
          </div>

          {step === 'code' ? (
            /* Referral Code Step */
            <div>
              {error && (
                <div className="mb-6 p-4 rounded-[14px]" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <p className="text-sm" style={{ color: '#dc2626' }}>No account found. Please enter a referral code to get started.</p>
                </div>
              )}
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider mb-4" style={{ background: 'rgba(13,148,136,0.08)', color: '#0d9488' }}>
                Early access
              </div>
              <h2 className="text-2xl font-extrabold mb-2" style={{ color: '#1e293b', letterSpacing: '-0.03em' }}>
                Enter your referral code
              </h2>
              <p className="text-sm mb-8" style={{ color: '#64748b' }}>
                arrivo is currently referral-only. Enter your code below to get started.
              </p>

              <form onSubmit={handleCodeSubmit}>
                <div className="mb-5">
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => {
                      setInviteCode(e.target.value.toUpperCase());
                      setCodeError('');
                    }}
                    placeholder="XXXX-XXXX-XXXX-XXXX-XXXXX"
                    className="w-full px-4 py-3.5 text-base font-mono tracking-wider rounded-[14px] border-2 focus:outline-none transition-all"
                    style={{
                      borderColor: codeError ? '#fca5a5' : 'rgba(31,29,59,0.08)',
                      background: codeError ? '#fef2f2' : 'white',
                      color: '#1e293b',
                    }}
                    onFocus={(e) => !codeError && (e.target.style.boxShadow = '0 0 0 2px #2dd4bf')}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                    autoFocus
                    spellCheck={false}
                    autoComplete="off"
                  />
                  {codeError && (
                    <p className="mt-2 text-sm" style={{ color: '#ef4444' }}>{codeError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isValidating}
                  className="w-full py-3.5 rounded-[14px] font-bold text-white text-sm transition-all"
                  style={{
                    background: isValidating ? '#94a3b8' : 'linear-gradient(135deg, #0d9488, #0f766e)',
                    boxShadow: isValidating ? 'none' : '0 10px 24px rgba(13,148,136,0.2)',
                    cursor: isValidating ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isValidating ? 'Validating...' : 'Continue'}
                </button>
              </form>

              <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(31,29,59,0.06)' }}>
                <p className="text-sm text-center" style={{ color: '#94a3b8' }}>
                  Don't have a referral code?{' '}
                  <a
                    href="mailto:hello@arrivotravel.ai"
                    className="font-bold hover:underline"
                    style={{ color: '#0d9488' }}
                  >
                    Email us
                  </a>
                  {' '}or DM{' '}
                  <a
                    href="https://instagram.com/arrivotravel.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold hover:underline"
                    style={{ color: '#0d9488' }}
                  >
                    @arrivotravel.ai
                  </a>
                </p>
                <p className="text-sm text-center mt-4">
                  <button
                    onClick={() => setStep('login')}
                    className="font-bold hover:underline"
                    style={{ color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Already have an account? Sign in
                  </button>
                </p>
              </div>
            </div>
          ) : (
            /* Google Sign In Step */
            <div>
              <h2 className="text-2xl font-extrabold mb-2" style={{ color: '#1e293b', letterSpacing: '-0.03em' }}>
                Welcome to arrivo
              </h2>
              <p className="text-sm mb-8" style={{ color: '#64748b' }}>
                Sign in to start planning your next adventure.
              </p>

              {error && (
                <div className="mb-6 p-4 rounded-[14px]" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <p className="text-sm" style={{ color: '#dc2626' }}>{error}</p>
                </div>
              )}

              {/* Features */}
              <div className="mb-8 space-y-4">
                {[
                  { title: 'AI-powered itineraries', sub: 'Personalized plans in seconds',
                    svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
                  { title: 'Real restaurants & activities', sub: 'Verified places, not generic lists',
                    svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg> },
                  { title: 'Your taste, remembered', sub: 'Smarter suggestions over time',
                    svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg> },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(13,148,136,0.08)' }}>{f.svg}</div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#1e293b' }}>{f.title}</p>
                      <p className="text-xs" style={{ color: '#94a3b8' }}>{f.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Terms checkbox */}
              <label className="flex items-start gap-3 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    if (e.target.checked) setTermsError('');
                  }}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                />
                <span className="text-xs" style={{ color: '#64748b' }}>
                  I agree to the{' '}
                  <a href="https://arrivotravel.ai/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#0d9488' }}>Terms of Service</a>{' '}
                  and{' '}
                  <a href="https://arrivotravel.ai/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#0d9488' }}>Privacy Policy</a>
                </span>
              </label>

              <label className="flex items-start gap-3 mb-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => {
                    setAgeConfirmed(e.target.checked);
                    if (e.target.checked) setTermsError('');
                  }}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                />
                <span className="text-xs" style={{ color: '#64748b' }}>
                  I confirm I am 16 years of age or older
                </span>
              </label>
              {termsError && (
                <p className="mb-4 text-xs" style={{ color: '#ef4444' }}>{termsError}</p>
              )}

              {/* Google Sign In Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={isSigningIn || loading}
                className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-[14px] border-2 transition-all"
                style={{
                  borderColor: acceptedTerms && ageConfirmed ? 'rgba(31,29,59,0.08)' : 'rgba(31,29,59,0.04)',
                  background: acceptedTerms && ageConfirmed ? 'white' : '#f8fafc',
                  cursor: acceptedTerms && ageConfirmed ? 'pointer' : 'not-allowed',
                  opacity: acceptedTerms && ageConfirmed ? 1 : 0.6,
                  boxShadow: acceptedTerms && ageConfirmed ? '0 4px 16px rgba(28,41,53,0.05)' : 'none',
                }}
              >
                {isSigningIn || loading ? (
                  <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: '#0d9488', borderTopColor: 'transparent' }} />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                <span className="font-bold text-sm" style={{ color: acceptedTerms && ageConfirmed ? '#1e293b' : '#94a3b8' }}>
                  {isSigningIn || loading ? 'Signing in...' : 'Continue with Google'}
                </span>
              </button>


              <button
                onClick={() => setStep('code')}
                className="mt-5 w-full text-xs flex items-center justify-center gap-1.5 transition-colors"
                style={{ color: '#94a3b8' }}
                onMouseEnter={(e) => e.target.style.color = '#64748b'}
                onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Enter a different code
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="mt-10 pt-6 flex items-center justify-between text-[11px]" style={{ borderTop: '1px solid rgba(31,29,59,0.06)', color: '#94a3b8' }}>
            <p>&copy; 2026 arrivo</p>
            <div className="flex items-center gap-4">
              <a href="https://arrivotravel.ai/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy</a>
              <a href="https://arrivotravel.ai/terms" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms</a>
              <a href="mailto:hello@arrivotravel.ai" className="hover:underline">Contact</a>
            </div>
          </div>
        </div>
      </div>

      {/* Right side — sample trips showcase with hero image */}
      <div className="hidden lg:flex lg:w-1/2 flex-col relative overflow-hidden">
        {/* Hero background image */}
        <div className="absolute inset-0">
          <img src="/hero-home.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        {/* Dark overlay for readability */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(49,49,67,0.88) 0%, rgba(49,49,67,0.75) 50%, rgba(13,148,136,0.6) 100%)' }} />
        {/* Topo texture overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `url("${TOPO_SVG}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.08,
        }} />

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-10 py-12 max-w-xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider mb-4" style={{ background: 'rgba(45,212,191,0.15)', color: '#2dd4bf' }}>
              Real trips. Real places.
            </div>
            <h2 className="text-3xl font-extrabold mb-3" style={{ color: '#ffffff', letterSpacing: '-0.04em', lineHeight: 1.1 }}>
              See what Wanderers<br />are planning
            </h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)', maxWidth: 400 }}>
              AI-crafted itineraries with real restaurants, activities, and local tips — built in minutes.
            </p>
          </div>

          {/* Sample trip cards */}
          <div className="space-y-3">
            {SAMPLE_TRIPS.map((trip, idx) => (
              <div
                key={idx}
                className="rounded-[22px] border overflow-hidden transition-all"
                style={{
                  borderColor: 'rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                }}
              >
                {/* Card header */}
                <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: trip.color }}>
                    DAY {trip.dayNum}
                  </span>
                  <span className="text-[11px] font-semibold flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {trip.flag} {trip.dest}
                  </span>
                </div>

                {/* Day title */}
                <div className="px-5 pb-2">
                  <h3 className="text-sm font-extrabold" style={{ color: '#ffffff', letterSpacing: '-0.02em' }}>
                    {trip.title}
                  </h3>
                </div>

                {/* Activities */}
                <div className="px-5 pb-3">
                  {trip.activities.map((act, i) => (
                    <div key={i} className="flex items-center gap-2.5 py-1.5" style={{ borderBottom: i < trip.activities.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                      <span className="text-[10px] font-bold w-14 text-right flex-shrink-0" style={{ color: trip.color }}>{act.time}</span>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: trip.color, opacity: 0.6 }} />
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>{act.name}</span>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="px-5 py-2" style={{ background: 'rgba(255,255,255,0.05)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <span className="text-[10px] font-semibold" style={{ color: 'rgba(255,255,255,0.4)' }}>{trip.days} days · {trip.activities.length * trip.days}+ activities</span>
                </div>
              </div>
            ))}
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-2 mt-6">
            <div className="flex -space-x-2">
              {['E', 'M', 'Y', 'S'].map((initial, i) => (
                <div key={i} className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-extrabold border-2 border-white" style={{
                  background: ['#0d9488', '#CD7A4D', '#6366f1', '#E11D48'][i],
                }}>
                  {initial}
                </div>
              ))}
            </div>
            <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.5)' }}>
              10,000+ trips planned by wanderers worldwide
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullPageLogin;
